import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth';
import { StreakPanel } from './StreakPanel';
import './StreakBanner.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface StreakInfo {
  current_streak: number;
  longest_streak: number;
  streak_active_today: boolean;
}

let _bannerShownThisSession = false;

export function StreakBanner() {
  const { token } = useAuth();
  const [info, setInfo] = useState<StreakInfo | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!token) { setInfo(null); return; }
    let aborted = false;
    fetch(`${API}/stats/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (aborted || !d) return;
        if (typeof d.current_streak !== 'number') return;
        setInfo({
          current_streak: d.current_streak,
          longest_streak: d.longest_streak,
          streak_active_today: !!d.streak_active_today,
        });
      })
      .catch(() => {});
    return () => { aborted = true; };
  }, [token]);

  useEffect(() => {
    if (!info || info.current_streak < 1) return;
    if (_bannerShownThisSession) return;
    _bannerShownThisSession = true;
    setVisible(true);
    setTimeout(() => setVisible(false), 3000);
  }, [info]);

  if (!info || info.current_streak < 1) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="streak-banner-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setVisible(false)}
        >
          <motion.div
            className="streak-banner-popup"
            initial={{ 
              scale: 0.2, 
              rotate: -15,
              x: 160,
              y: 160,
              opacity: 0 
            }}
            animate={{ 
              scale: 1, 
              rotate: 0,
              x: 0,
              y: 0,
              opacity: 1 
            }}
            exit={{ 
              scale: 0.3, 
              rotate: 10,
              x: 120,
              y: 120,
              opacity: 0 
            }}
            transition={{
              type: 'spring',
              stiffness: 90,
              damping: 7,
            }}
            onClick={e => e.stopPropagation()}
          >
            <button className="streak-banner-close" onClick={() => setVisible(false)} aria-label="Kapat">×</button>
            <StreakPanel />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
